import { coerceNumberProperty } from '@angular/cdk/coercion';
import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Inject, InjectionToken, Input, Optional } from '@angular/core';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';


/** Possible mode for a progress spinner. */
export type ProgressSpinnerMode = 'determinate' | 'indeterminate';

/**
 * Base reference size of the spinner.
 * @internal
 */
const BASE_SIZE = 100;

/**
 * Base reference stroke width of the spinner.
 * @internal
 */
const BASE_STROKE_WIDTH = 10;

// Boilerplate for applying mixins to Spinner.
/** @internal */
export class ProgressSpinnerBase {
  constructor(public _elementRef: ElementRef) {}
}

/** Default `uex-spinner` options that can be overridden. */
/** @internal */
export interface ProgressSpinnerDefaultOptions {
  /** Diameter of the spinner. */
  diameter?: number;
  /** Width of the spinner's stroke. */
  strokeWidth?: number;
  /**
   * Whether the animations should be force to be enabled, ignoring if the current environment is
   * using NoopAnimationsModule.
   */
  _forceAnimations?: boolean;
}

/** Injection token to be used to override the default options for `uex-spinner`. */
/** @internal */
export const UEX_PROGRESS_SPINNER_DEFAULT_OPTIONS =
    new InjectionToken<ProgressSpinnerDefaultOptions>('uex-spinner-default-options', {
      providedIn: 'root',
      factory: UEX_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY,
    });

/** @internal */
export function UEX_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY(): ProgressSpinnerDefaultOptions {
  return {diameter: BASE_SIZE};
}

// .0001 percentage difference is necessary in order to avoid unwanted animation frames
// for example because the animation duration is 4 seconds, .1% accounts to 4ms
// which are enough to see the flicker described in
// https://github.com/angular/material2/issues/8984
/** @internal */
const INDETERMINATE_ANIMATION_TEMPLATE = `
 @keyframes uex-spinner-stroke-rotate-DIAMETER {
    0%      { stroke-dashoffset: START_VALUE;  transform: rotate(0); }
    12.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(0); }
    12.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(72.5deg); }
    25%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(72.5deg); }
    25.0001%   { stroke-dashoffset: START_VALUE;  transform: rotate(270deg); }
    37.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(270deg); }
    37.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(161.5deg); }
    50%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(161.5deg); }
    50.0001%  { stroke-dashoffset: START_VALUE;  transform: rotate(180deg); }
    62.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(180deg); }
    62.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(251.5deg); }
    75%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(251.5deg); }
    75.0001%  { stroke-dashoffset: START_VALUE;  transform: rotate(90deg); }
    87.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(90deg); }
    87.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(341.5deg); }
    100%    { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(341.5deg); }
  }
`;

/**
 * `<uex-spinner>` component.
 */
@Component({
  selector: 'uex-spinner',
  exportAs: 'ProgressSpinner',
  host: {
    'role': 'progressbar',
    'class': 'uex-spinner',
    '[class._uex-animation-noopable]': `_noopAnimations`,
    '[style.width.px]': 'diameter',
    '[style.height.px]': 'diameter',
    '[attr.mode]': 'mode',
    '[attr.aria-valuemin]': 'mode === "determinate" ? 0 : null',
    '[attr.aria-valuemax]': 'mode === "determinate" ? 100 : null',
    '[attr.aria-valuenow]': 'mode === "determinate" ? value : null'
  },
  inputs: ['color'],
  templateUrl: 'spinner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Spinner {

  /** The diameter of the progress spinner (will set width and height of svg). */
  @Input()
  get diameter(): number { return this._diameter; }
  set diameter(size: number) {
    this._diameter = coerceNumberProperty(size);

    if (!this._fallbackAnimation && !Spinner.diameters.has(this._diameter)) {
      this._attachStyleNode();
    }
  }

  /** Stroke width of the progress spinner. */
  @Input()
  get strokeWidth(): number {
    return this._strokeWidth || this.diameter / 10;
  }
  set strokeWidth(value: number) {
    this._strokeWidth = coerceNumberProperty(value);
  }

  /** Mode of the progress circle */
  @Input()
  get mode(): ProgressSpinnerMode {
    return this._mode;
  }
  set mode(newValue: ProgressSpinnerMode) {
    this._mode = newValue ? newValue : 'indeterminate';

    // On IE and Edge, we can't animate the `stroke-dashoffset`
    // reliably so we fall back to a non-spec animation.
    const animationClass = `uex-spinner-${this.mode}${this._fallbackAnimation ? '-fallback' : ''}-animation`;

    if (this._oldMode) { this._elementRef.nativeElement.classList.remove(this._oldMode); }
    this._oldMode = animationClass;
    this._elementRef.nativeElement.classList.add(animationClass);
  }

  /** Value of the progress circle. */
  @Input()
  get value(): number {
    return this.mode === 'determinate' ? this._value : 0;
  }
  set value(newValue: number) {
    this._value = Math.max(0, Math.min(100, coerceNumberProperty(newValue)));
  }

  /** Color of the progress circle. */
  @Input()
  get color(): string {
    return this._color;
  }
  set color(newValue: string) {
    this._color = newValue;
    this._updateColor();
  }

  constructor(public _elementRef: ElementRef,
              platform: Platform,
              @Optional() @Inject(DOCUMENT) private _document: any,
              // @breaking-change 8.0.0 animationMode and defaults parameters to be made required.
              @Optional() @Inject(ANIMATION_MODULE_TYPE) private animationMode?: string,
              @Inject(UEX_PROGRESS_SPINNER_DEFAULT_OPTIONS)
                  private defaults?: ProgressSpinnerDefaultOptions) {

    this._fallbackAnimation = platform.EDGE || platform.TRIDENT;

    if (defaults) {
      if (defaults.diameter) {
        this.diameter = defaults.diameter;
      }

      if (defaults.strokeWidth) {
        this.strokeWidth = defaults.strokeWidth;
      }
    }

    if (!this._mode) { this.mode = 'indeterminate'; }
    if (!this._color) { this.color = 'primary'; }
  }

  /** The radius of the spinner, adjusted for stroke width. */
  get _circleRadius() {
    return (this.diameter - BASE_STROKE_WIDTH) / 2;
  }

  /** The view box of the spinner's svg element. */
  get _viewBox() {
    const viewBox = this._circleRadius * 2 + this.strokeWidth;
    return `0 0 ${viewBox} ${viewBox}`;
  }

  /** The stroke circumference of the svg circle. */
  get _strokeCircumference(): number {
    return 2 * Math.PI * this._circleRadius;
  }

  /** The dash offset of the svg circle. */
  get _strokeDashOffset() {
    if (this.mode === 'determinate') {
      return this._strokeCircumference * (100 - this._value) / 100;
    }

    // In fallback mode set the circle to 80% and rotate it with CSS.
    if (this._fallbackAnimation && this.mode === 'indeterminate') {
      return this._strokeCircumference * 0.2;
    }

    return null;
  }

  /** Stroke width of the circle in percent. */
  get _circleStrokeWidth() {
    return this.strokeWidth / this.diameter * 100;
  }

  /** Tracks diameters of existing instances to de-dupe generated styles (default d = 100) */
  private static diameters = new Set<number>([BASE_SIZE]);

  /**
   * Used for storing all of the generated keyframe animations.
   * @dynamic
   */
  private static styleTag: HTMLStyleElement|null = null;

  private _value = 0;
  private _color: string;
  private _oldColor: string = null;
  private _mode: ProgressSpinnerMode;
  private _oldMode: string = null;
  private _strokeWidth: number;
  private _fallbackAnimation = false;

  /** Whether the _uex-animation-noopable class should be applied, disabling animations.  */
  _noopAnimations: boolean = this.animationMode === 'NoopAnimations' && (
      !!this.defaults && !this.defaults._forceAnimations);
  private _diameter = BASE_SIZE;

  /** Dynamically generates a style tag containing the correct animation for this diameter. */
  private _attachStyleNode(): void {
    let styleTag = Spinner.styleTag;

    if (!styleTag) {
      styleTag = this._document.createElement('style');
      this._document.head.appendChild(styleTag);
      Spinner.styleTag = styleTag;
    }

    if (styleTag && styleTag.sheet) {
      (styleTag.sheet as CSSStyleSheet).insertRule(this._getAnimationText(), 0);
    }

    Spinner.diameters.add(this.diameter);
  }

  /** Generates animation styles adjusted for the spinner's diameter. */
  private _getAnimationText(): string {
    return INDETERMINATE_ANIMATION_TEMPLATE
        // Animation should begin at 5% and end at 80%
        .replace(/START_VALUE/g, `${0.95 * this._strokeCircumference}`)
        .replace(/END_VALUE/g, `${0.2 * this._strokeCircumference}`)
        .replace(/DIAMETER/g, `${this.diameter}`);
  }

  /** Set the color class of spinner */
  private _updateColor() {
    const current_classes = this._elementRef.nativeElement.className.split(' ');
    if (this._oldColor) {
      const index = current_classes.indexOf(`uex-spinner-${this._oldColor}`);
      if (index > -1) { current_classes.splice(index, 1); }
    }

    this._oldColor = null;
    if (this._color) {
      this._oldColor = this._color;
      current_classes.push(`uex-spinner-${this._color}`);
    }
    this._elementRef.nativeElement.className = current_classes.join(' ');
  }
}
